/**
 * useSecondaryQueryState -- state machine for secondary data (FEAT-052 TASK-008, design.md A.3).
 *
 * Takes the object returned by TanStack `useQuery` (only the fields below are read) and tells a
 * consumer what to show: a delayed loading text (no flashing), a failure with retry, or a
 * permission denial (403/404, no retry).
 */

import { useCallback, useEffect, useState } from 'react';
import { isAccessDeniedError } from '@/utils/apiErrors';

export interface SecondaryQueryLike {
  isLoading: boolean;
  isFetching?: boolean;
  isError: boolean;
  error?: unknown;
  refetch: () => unknown;
}

export interface SecondaryQueryState {
  /** True while a load is in progress (first load or retry after an error); the live region must exist. */
  isPending: boolean;
  /** True once the load has taken longer than `delayMs`: show the loading text. */
  showLoading: boolean;
  /** True when the query failed and no retry is running. */
  showError: boolean;
  /** True when the failure is a denial (403/404): no retry offered. */
  isForbidden: boolean;
  retry: () => void;
}

export const SECONDARY_LOADING_DELAY_MS = 300;

export function useSecondaryQueryState(
  q: SecondaryQueryLike,
  { delayMs = SECONDARY_LOADING_DELAY_MS }: { delayMs?: number } = {},
): SecondaryQueryState {
  const isPending = q.isLoading || (q.isError && q.isFetching === true);
  const [elapsed, setElapsed] = useState(false);

  useEffect(() => {
    if (!isPending) {
      setElapsed(false);
      return undefined;
    }
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [isPending, delayMs]);

  const { refetch } = q;
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const showError = q.isError && !isPending;
  return {
    isPending,
    showLoading: isPending && elapsed,
    showError,
    isForbidden: showError && isAccessDeniedError(q.error),
    retry,
  };
}
